import { useEffect, useRef, useState } from "react";
import {
  Boxes, Menu, X, ChevronDown,
  Warehouse, Truck,
  Target, ClipboardCheck, TrendingUp,
  PlayCircle, Smartphone,
  Rocket, Cpu, Network, ScanSearch, LayoutDashboard, Package,
  Globe, Info, Mail, Linkedin,
  Lightbulb, Handshake,
} from "lucide-react";
import axios from "axios";
import { Button } from "./ui/button";
import MobileNavTree from "./MobileNavTree";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = "primes4.brand.logo.v1";

// --- Navigation config -----------------------------------------------------
// Single source of truth for the PrimeS4 AI ecosystem top nav.
// Items with `children` render a dropdown panel. Items with `comingSoon: true`
// render as a muted, non-interactive row with a small "Coming soon" hint.
// `icon` is an optional lucide-react component rendered as a 14–16px
// monochrome line icon in front of the label (SAP/Fiori-style).
//
// Platforms uses a bespoke compact indented panel; Enterprise Apps is a flat
// single-column dropdown.
const NAV_ITEMS = [
  { label: "Home", href: "/" },
  { label: "PRIVA", href: "/priva" },
  {
    label: "Platforms",
    href: "/platforms",
    children: [
      {
        label: "WarehouseX",
        href: "/warehousex",
        icon: Warehouse,
        children: [
          {
            label: "SmartStrategy",
            href: "/warehousex/smartstrategy",
            icon: Target,
            children: [
              { label: "SmartEvaluate", href: "/warehousex/smartstrategy/smartevaluate", icon: ClipboardCheck },
              { label: "SmartImprove", href: "/warehousex/smartstrategy/smartimprove", icon: TrendingUp },
            ],
          },
          {
            label: "SmartExecution",
            href: "/warehousex/smartexecution",
            icon: PlayCircle,
            children: [
              { label: "SmartRF", href: "/warehousex/smartexecution/smartrf", icon: Smartphone },
            ],
          },
          { label: "SmartAcceleration", href: "/warehousex/smartacceleration", icon: Rocket },
          { label: "SmartWarehouse Tech", href: "/warehousex/smartwarehouse-tech", icon: Cpu },
          {
            label: "SmartIntegrations",
            href: "/warehousex/smartintegrations",
            icon: Network,
            children: [
              { label: "SmartInspect", href: "/warehousex/smartintegrations/smartinspect", icon: ScanSearch },
            ],
          },
        ],
      },
      { label: "TransportX", comingSoon: true, icon: Truck },
    ],
  },
  {
    label: "Enterprise Apps",
    href: "/enterprise-apps",
    children: [
      { label: "SmartInspect", href: "/warehousex/smartintegrations/smartinspect", icon: ScanSearch },
      { label: "SmartRF", href: "/warehousex/smartexecution/smartrf", icon: Smartphone },
      { label: "SmartEWM", comingSoon: true, icon: Package },
      { label: "SmartCenter", comingSoon: true, icon: LayoutDashboard },
    ],
  },
  {
    label: "Innovation",
    href: "/innovation",
    children: [
      { label: "Innovation", href: "/innovation", icon: Lightbulb },
      { label: "Co-Innovate", href: "/coinnovate", icon: Handshake },
    ],
  },
  {
    label: "PrimeS4 Group",
    children: [
      { label: "Main Website",     href: "https://primes4.com/",                                       icon: Globe,    external: true },
      { label: "About PrimeS4",    href: "https://primes4.com/our-company/",                           icon: Info,     external: true },
      { label: "Contact PrimeS4",  href: "https://primes4.com/contact_us/",                            icon: Mail,     external: true },
      { label: "LinkedIn",         href: "https://www.linkedin.com/company/primes4-technologies-llc/", icon: Linkedin, external: true },
    ],
  },
];

const slug = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.logo === "string") return parsed;
  } catch { /* ignore */ }
  return null;
}

function writeCache(logo) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ logo, fetched_at: Date.now() })); }
  catch { /* quota exceeded — not critical */ }
}

// --- Small atoms -----------------------------------------------------------
// Compact, low-key "coming soon" hint. Intentionally not a pill/badge — small
// italic muted text keeps the menu calm and enterprise-clean.
const ComingSoonHint = () => (
  <span
    className="ml-auto text-[11px] italic text-[#9CA3AF] tracking-normal whitespace-nowrap"
    data-testid="nav-coming-soon-hint"
  >
    Coming soon
  </span>
);

// Small monochrome line icon. Falls back to nothing if no icon was provided.
// Size kept at 14px to read as a glyph next to the label, not a UI element.
const NavIcon = ({ icon: Icon, muted = false }) => {
  if (!Icon) return <span aria-hidden className="w-3.5 h-3.5 inline-block" />;
  return (
    <Icon
      className={`w-3.5 h-3.5 shrink-0 ${muted ? "text-[#C7CBD1]" : "text-[#6B7280] group-hover:text-[#025979]"}`}
      strokeWidth={1.5}
      aria-hidden="true"
    />
  );
};

/**
 * DropdownLink — single row inside a dropdown panel.
 * - `indent` 0|1|2 controls left padding (parent / child / grandchild).
 * - `emphasis` "primary" boosts type weight (used for pillars).
 * - `comingSoon` renders a muted, non-interactive row.
 */
const DropdownLink = ({ item, indent = 0, emphasis = "default", testIdPrefix }) => {
  const padLeft = { 0: "pl-3", 1: "pl-6", 2: "pl-10" }[indent] || "pl-3";
  const weight = emphasis === "primary" ? "font-medium" : "font-normal";
  const size = indent >= 2 ? "text-[13px]" : "text-sm";
  const base = `group flex items-center gap-2.5 ${padLeft} pr-3 py-1.5 ${size} ${weight} rounded-sm transition-colors`;
  if (item.comingSoon) {
    return (
      <div
        className={`${base} text-[#9CA3AF] cursor-not-allowed select-none`}
        data-testid={`${testIdPrefix}-${slug(item.label)}`}
        aria-disabled="true"
      >
        <NavIcon icon={item.icon} muted />
        <span className="whitespace-nowrap">{item.label}</span>
        <ComingSoonHint />
      </div>
    );
  }
  return (
    <a
      href={item.href}
      target={item.external ? "_blank" : undefined}
      rel={item.external ? "noopener noreferrer" : undefined}
      className={`${base} text-[#1F2937] hover:bg-[#F3F4F6] hover:text-[#025979]`}
      data-testid={`${testIdPrefix}-${slug(item.label)}`}
    >
      <NavIcon icon={item.icon} />
      <span className="whitespace-nowrap">{item.label}</span>
    </a>
  );
};

/**
 * PlatformsPanel — bespoke compact indented panel showing the full WarehouseX
 * hierarchy (3 levels) plus other platforms underneath.
 * Visual hierarchy:
 *   • Platform header: small caps eyebrow + brand-blue.
 *   • Pillars (level 1): medium weight + small line icon.
 *   • Modules (level 2): lighter weight, smaller, extra indent.
 *   • Pillars are separated by a small vertical gap so groups read as groups.
 *   • Coming-soon platforms live under a thin top divider so they don't
 *     compete with the active hierarchy.
 */
const PlatformsPanel = ({ items }) => {
  const active = items.filter((p) => !p.comingSoon && p.children);
  const inactive = items.filter((p) => p.comingSoon || !p.children);
  return (
    <div
      role="menu"
      data-testid="nav-platforms-panel"
      className="min-w-[300px] py-2"
    >
      {active.map((platform) => (
        <div key={platform.label} className="px-1.5 pt-1 pb-1.5">
          {/* Platform header — clickable link to the platform landing */}
          <a
            href={platform.href}
            data-testid={`nav-platforms-${slug(platform.label)}`}
            className="group flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold tracking-[0.18em] text-[#025979] uppercase hover:text-[#03738C]"
          >
            <NavIcon icon={platform.icon} />
            <span>{platform.label}</span>
          </a>
          <div className="flex flex-col">
            {platform.children.map((pillar, idx) => (
              <div
                key={pillar.label}
                className={idx === 0 ? "mt-0.5" : "mt-1.5"}
              >
                <DropdownLink
                  item={pillar}
                  indent={1}
                  emphasis="primary"
                  testIdPrefix={`nav-platforms-${slug(platform.label)}`}
                />
                {pillar.children?.map((mod) => (
                  <DropdownLink
                    key={mod.label}
                    item={mod}
                    indent={2}
                    testIdPrefix={`nav-platforms-${slug(platform.label)}-${slug(pillar.label)}`}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
      {inactive.length > 0 && (
        <div className="mt-1 border-t border-[#F3F4F6] pt-1 px-1.5 pb-1">
          {inactive.map((p) => (
            <DropdownLink
              key={p.label}
              item={p}
              indent={0}
              emphasis="primary"
              testIdPrefix="nav-platforms"
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FlatPanel = ({ items, testIdPrefix }) => (
  <div
    role="menu"
    data-testid={`${testIdPrefix}-panel`}
    className="min-w-[280px] py-2 px-1.5"
  >
    {items.map((it) => (
      <DropdownLink
        key={it.label}
        item={it}
        indent={0}
        emphasis="primary"
        testIdPrefix={testIdPrefix}
      />
    ))}
  </div>
);

// --- Desktop dropdown wrapper ---------------------------------------------
/**
 * NavDropdown — top-level nav item with a hover/focus dropdown.
 * Uses a small open/close delay so cursor travel between trigger and panel
 * doesn't dismiss the menu prematurely.
 */
const NavDropdown = ({ item, textColor, navHover, isOverlay }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const containerRef = useRef(null);

  const cancelClose = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    timer.current = setTimeout(() => setOpen(false), 120);
  };

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const triggerId = `nav-${slug(item.label)}`;
  const isPlatforms = item.label === "Platforms";
  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      data-testid={`${triggerId}-dropdown`}
    >
      {item.href ? (
        <a
          href={item.href}
          aria-haspopup="menu"
          aria-expanded={open ? "true" : "false"}
          data-testid={triggerId}
          className={`inline-flex items-center gap-1 text-sm font-medium transition-colors ${textColor} ${navHover}`}
        >
          {item.label}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </a>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open ? "true" : "false"}
          data-testid={triggerId}
          className={`inline-flex items-center gap-1 text-sm font-medium transition-colors ${textColor} ${navHover}`}
        >
          {item.label}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}
      {open && (
        <div
          className={`absolute left-0 top-full pt-2 z-50 ${isOverlay ? "" : ""}`}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div className="rounded-sm border border-[#E5E7EB] bg-white shadow-lg">
            {isPlatforms ? (
              <PlatformsPanel items={item.children} />
            ) : (
              <FlatPanel items={item.children} testIdPrefix={triggerId} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Header
 * Props:
 *   variant = "solid" (default) | "transparent"
 *     transparent = transparent bg + white text on top of hero; flips to solid
 *     white + dark text after the user scrolls past ~40px.
 */
export const Header = ({ variant = "solid" }) => {
  const cached = readCache();
  const [logo, setLogo] = useState(cached ? cached.logo : "");
  const [resolved, setResolved] = useState(cached !== null);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/branding/public`)
      .then((r) => {
        if (cancelled) return;
        const b = r.data || {};
        const next = b.platform_logo || "";
        setLogo(next);
        setResolved(true);
        writeCache(next);
      })
      .catch(() => { if (!cancelled) setResolved(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (variant !== "transparent") return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [variant]);

  const isOverlay = variant === "transparent" && !scrolled;

  const headerBg = isOverlay
    ? "bg-transparent"
    : "bg-white border-b border-[#E5E7EB] shadow-sm";
  const textColor = isOverlay ? "text-white" : "text-[#0F172A]";
  const navHover = isOverlay ? "hover:text-[#5EB8D8]" : "hover:text-[#025979]";
  const logoTextColor = isOverlay ? "text-white" : "text-[#0F172A]";
  const ctaClasses = isOverlay
    ? "border border-white/40 bg-white/5 text-white hover:bg-white/15 hover:border-white/70"
    : "bg-[#025979] hover:bg-[#03738C] text-white";

  return (
    <header
      data-testid="app-header"
      data-variant={variant}
      data-overlay={isOverlay ? "true" : "false"}
      className={`sticky top-0 z-50 transition-colors duration-300 ${headerBg}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <a
          href="/"
          className="flex items-center gap-2.5"
          data-testid="logo-container"
        >
          {logo ? (
            <img loading="eager" fetchpriority="high"
              src={logo}
              alt="Platform logo"
              className="h-8 w-auto object-contain"
              data-testid="header-brand-logo"
            />
          ) : resolved ? (
            <>
              <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${isOverlay ? "bg-white" : "bg-[#025979]"}`}>
                <Boxes className={`w-5 h-5 ${isOverlay ? "text-[#025979]" : "text-white"}`} strokeWidth={1.5} />
              </div>
              <span
                className={`font-semibold text-lg tracking-tight ${logoTextColor}`}
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                PrimeS4
              </span>
            </>
          ) : (
            <div aria-hidden="true" data-testid="header-brand-skeleton" className="h-8 w-32" />
          )}
        </a>

        {/* Desktop nav */}
        <nav data-testid="primary-nav" className="hidden lg:flex items-center gap-6">
          {NAV_ITEMS.map((item) =>
            item.children ? (
              <NavDropdown
                key={item.label}
                item={item}
                textColor={textColor}
                navHover={navHover}
                isOverlay={isOverlay}
              />
            ) : (
              <a
                key={item.label}
                href={item.href}
                data-testid={`nav-${slug(item.label)}`}
                className={`text-sm font-medium transition-colors ${textColor} ${navHover}`}
              >
                {item.label}
              </a>
            )
          )}
        </nav>

        {/* Right cluster: Login + Request Demo */}
        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href="/transformation"
            data-testid="header-login-btn"
            className={`hidden sm:inline-flex items-center rounded-sm h-10 px-4 text-xs font-semibold border transition-colors ${
              isOverlay
                ? "border-white/40 text-white hover:bg-white/10 hover:border-white"
                : "border-[#E5E7EB] text-[#0F172A] hover:bg-[#F3F4F6] hover:border-[#025979]/40"
            }`}
          >
            Login
          </a>
          <Button
            data-testid="header-cta-request-demo"
            onClick={() => { window.dispatchEvent(new CustomEvent("requestDemo:open")); }}
            className={`hidden sm:inline-flex rounded-sm h-10 px-5 text-xs font-semibold transition-colors ${ctaClasses}`}
          >
            Request Demo
          </Button>
          <button
            type="button"
            data-testid="header-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className={`lg:hidden p-1.5 rounded-sm transition-colors ${textColor} ${isOverlay ? "hover:bg-white/10" : "hover:bg-[#F3F4F6]"}`}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {open && (
        <div
          data-testid="mobile-nav"
          className="lg:hidden bg-white border-t border-[#E5E7EB] shadow-lg max-h-[calc(100vh-64px)] overflow-y-auto"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col">
            <MobileNavTree items={NAV_ITEMS} onNavigate={() => setOpen(false)} />
            <a
              href="/transformation"
              data-testid="mobile-login-btn"
              className="mt-4 inline-flex items-center justify-center rounded-sm h-10 px-4 text-sm font-semibold border border-[#E5E7EB] text-[#0F172A] hover:bg-[#F3F4F6]"
            >
              Login
            </a>
            <button
              type="button"
              data-testid="mobile-cta-request-demo"
              onClick={() => {
                setOpen(false);
                window.dispatchEvent(new CustomEvent("requestDemo:open"));
              }}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#025979] hover:bg-[#03738C] text-white rounded-sm h-10 px-4 text-sm font-semibold"
            >
              Request Demo
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
